import { LaunchPracticeInterviewResponse } from "api/resources/bePracticeInterview/types"
import {
  LearningItem,
  UserPlannerItemStatus,
} from "api/resources/learningItems/types"

export const getLearningContentTypeDisplay = (type: string): string => {
  switch (type) {
    case "pre_recorded_class":
      return "Recorded Class"
    case "ai_interview":
      return "Interview"
    case "cpat":
      return "Assessment"

    default:
      return type
  }
}

// Frontend-only planner items (AI Interview, CPAT) appended at the end of
// specific modules. Negative ids never collide with real backend ids.
export const SYNTHETIC_ITEM_IDS = {
  aiInterview: -1001,
  cpat: -1002,
} as const

export const isSyntheticItemId = (id: number): boolean => id < 0

export const AI_INTERVIEW_DURATION_MINUTES = 20
export const CPAT_DURATION_MINUTES = 60

/**
 * Builds the frontend-only "AI Interview" and "CPAT" steps appended to the
 * end of specific modules (see `learningItemSlugs.boostingEmployabilityV2`).
 * They're grafted onto the last real milestone so they participate in the
 * existing client-computed progress/grouping without any backend awareness.
 * Used both by the interactive planner (`usePlanner.tsx`) and the course
 * overview's curriculum preview (`LearningItems/Course/Overview`).
 */
export const buildSyntheticItems = (
  realItems: LearningItem[]
): LearningItem[] => {
  const lastItem = realItems[realItems.length - 1]
  if (!lastItem) return []

  const base = {
    milestone: lastItem.milestone,
    unlockDate: null,
    accessStatus: lastItem.accessStatus,
  }

  return [
    {
      ...base,
      id: SYNTHETIC_ITEM_IDS.aiInterview,
      learningContentId: SYNTHETIC_ITEM_IDS.aiInterview,
      sequence: lastItem.sequence + 1,
      status: "unlocked",
      learningContentType: "ai_interview",
      title: "Mock Interview",
    },
    {
      ...base,
      id: SYNTHETIC_ITEM_IDS.cpat,
      learningContentId: SYNTHETIC_ITEM_IDS.cpat,
      sequence: lastItem.sequence + 2,
      status: "unlocked",
      learningContentType: "cpat",
      title: "CPAT",
    },
  ]
}

/**
 * Derives the live 3-state status (Not Started/In Progress/Completed) for
 * the 2 synthetic items from the launch-API response and CPAT enrollment
 * progress, independent of any backend-provided `status` field.
 */
export const deriveSyntheticItemStatuses = ({
  launchData,
  cpatProgress,
}: {
  launchData: LaunchPracticeInterviewResponse | undefined
  cpatProgress: number
}): {
  aiInterviewStatus: UserPlannerItemStatus
  cpatStatus: UserPlannerItemStatus
} => {
  const aiInterviewStatus: UserPlannerItemStatus = !launchData
    ? "unlocked"
    : launchData.status === "complete"
    ? "completed"
    : launchData.invitationStatus === "started" ||
      launchData.invitationStatus === "submitted"
    ? "in_progress"
    : "unlocked"

  const cpatStatus: UserPlannerItemStatus =
    cpatProgress >= 100
      ? "completed"
      : cpatProgress > 0
      ? "in_progress"
      : "unlocked"

  return { aiInterviewStatus, cpatStatus }
}
